import {BrowserModule} from '@angular/platform-browser';
import {DatePipe, registerLocaleData} from '@angular/common';
import localePt from '@angular/common/locales/pt-PT';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {ErrorHandler, NgModule} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {SplitterModule} from 'primeng/splitter';
import {GlobalErrorHandler} from '@util/GlobalErrorHandler';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';

import {ModulesModule} from '@modules/modules.module';




import {AuthGuard} from '@guard/auth.guard';

import {AuthService} from '@service/auth.service';
import {UserService} from '@service/user.service';
import {NavigationService} from '@service/navigation.service';
import {GroupService} from '@service/group.service';
import {LoaderService} from '@service/loader.service';
import {ProfileService} from '@service/profile.service';
import {GeoPlaceService} from '@service/geoplace.service';
import {GeoCountyService} from '@service/geocounty.service';
import {GeoDistrictService} from '@service/geodistrict.service';

import {AuthInterceptor} from '@interceptor/auth.interceptor';
import {EnumInterceptor} from '@interceptor/enum.interceptor';
import {LoaderInterceptor} from '@interceptor/loader.interceptor';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {AddressService} from '@service/address.service';
import {NotificationService} from '@service/notification.service';
import {UserNotificationService} from '@service/user-notification.service';
import {UrbisService} from '@service/urbis.service';
import {GeoPostalCodeService} from '@service/geopostal-code.service';
import {GeocountryService} from '@service/geocountry.service';
import {AssetsService} from '@service/assets.service';
import {ConfirmationService, MessageService} from 'primeng/api';
import {providePrimeNG} from 'primeng/config';
// O `@primeng/themes` foi DEPRECADO ("no longer maintained. Please migrate to
// @primeuix/themes") e parou na 21.0.4, enquanto o primeng seguiu para a
// 21.1.9. Era, de resto, um reexport fino: o `definePreset` e o `palette`
// vinham do `@primeuix/styled` e os tipos do `@primeuix/themes/types`. A troca
// e so de caminho -- os simbolos e os presets sao os mesmos.
import {ColorScale, definePreset, palette} from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

import {ChallengeService} from '@service/challenge.service';
import {PrizeService} from '@service/prize.service';
import {AuctionService} from '@service/auction.service';
import {OffersService} from '@service/offers.service';


/**
 * Preset do projeto: Aura com a cor primaria do tema anterior.
 *
 * O `saga-blue`, usado ate ao hop 19-PN-TEMA, tinha `--primary-color: #2196F3`.
 * O Aura traz esmeralda (`#10b981`) por omissao, o que tornava verdes as tabs e
 * tudo o que segue a cor primaria. Decisao do parceiro humano no gate de
 * aprovacao: manter o azul de antes.
 *
 * A primitiva `blue` do Aura NAO serve -- e `#3b82f6`, o azul do Tailwind. O
 * helper `palette()` gera a escala 50..950 a partir de uma cor, com o 500
 * exatamente igual ao que se lhe da, que e o que o tema usa como primaria.
 */
const AUD_PRESET = definePreset(Aura, {
    semantic: {
        // O cast e da biblioteca, nao nosso: o `palette()` do @primeuix/styled
        // declara `string | ColorScale` porque devolve a string tal e qual
        // quando recebe uma referencia a token (`'{blue}'`). Com uma cor
        // literal devolve sempre a escala, e `semantic.primary` exige
        // `ColorScale`. Deixou de compilar no primeng 20, que apertou o tipo.
        primary: palette('#2196F3') as ColorScale,
    },
    components: {
        // Toasts: repor o aspeto do saga-blue.
        //
        // O Aura desenha-os "leves" -- fundo quase branco (`red.50` a 95% de
        // transparencia), borda fina a toda a volta e so o icone colorido. O
        // saga-blue desenhava-os "preenchidos": fundo saturado, texto escuro da
        // mesma familia e uma barra de 6px do lado esquerdo. Sao duas linguagens
        // visuais diferentes, e a segunda e a que a aplicacao tinha.
        //
        // Os valores sao os do proprio saga-blue, tirados do
        // `resources/themes/saga-blue/theme.css` do primeng 17 e nao inventados.
        //
        // NOTA: nao confundir isto com o bloco `.ui-toast-message` que existia
        // em `styles.scss`. Esse usava o naming do primeng 5/6 e estava morto ha
        // muitos majors -- foi removido neste hop. O aspeto antigo vinha todo do
        // tema, nunca do projeto.
        // Accordion: repor o aspeto do saga-blue.
        //
        // O Aura desenha-o liso -- cabecalho com fundo branco, sem borda, e os
        // paineis separados por uma linha em baixo. O saga-blue punha o
        // cabecalho numa barra cinzenta com borda a toda a volta.
        //
        // Valores tirados do `saga-blue/theme.css` do primeng 17. O que nao se
        // resolve por token e a POSICAO do chevron: no componente novo ele vem
        // depois do texto no DOM, e no antigo vinha antes. Isso esta em
        // styles.scss, com um `flex-direction: row-reverse`.
        accordion: {
            panel: {
                // Cada painel leva a sua propria borda pelo cabecalho e pelo
                // conteudo, como no saga-blue. Sem isto ficava uma linha a mais.
                borderWidth: '0',
            },
            header: {
                padding: '1rem',
                borderWidth: '1px',
                borderColor: '#dee2e6',
                borderRadius: '3px',
                color: '#495057',
                hoverColor: '#495057',
                activeColor: '#495057',
                background: '#f8f9fa',
                hoverBackground: '#f8f9fa',
                activeBackground: '#f8f9fa',
                activeHoverBackground: '#f8f9fa',
                toggleIcon: {
                    color: '#495057',
                    hoverColor: '#495057',
                    activeColor: '#495057',
                    activeHoverColor: '#495057',
                },
            },
            content: {
                padding: '1rem',
                borderWidth: '0 1px 1px 1px',
                borderColor: '#dee2e6',
                background: '#ffffff',
                color: '#495057',
            },
        },
        toast: {
            root: {
                // O saga-blue punha a borda so a esquerda, com 6px.
                borderWidth: '0 0 0 6px',
            },
            colorScheme: {
                light: {
                    info: {
                        background: '#b3e5fc',
                        borderColor: '#0891cf',
                        color: '#044868',
                        detailColor: '#044868',
                    },
                    success: {
                        background: '#c8e6c9',
                        borderColor: '#439446',
                        color: '#224a23',
                        detailColor: '#224a23',
                    },
                    warn: {
                        background: '#ffecb3',
                        borderColor: '#d9a300',
                        color: '#6d5100',
                        detailColor: '#6d5100',
                    },
                    error: {
                        background: '#ffcdd2',
                        borderColor: '#e60017',
                        color: '#73000c',
                        detailColor: '#73000c',
                    },
                },
            },
        },
    },
});

registerLocaleData(localePt);



export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}
